import React, { Component } from "react";
import { Modal, Spinner, Alert } from "react-bootstrap";
import { marked } from "marked";
import { MathJax, MathJaxContext } from "better-react-mathjax";

class AiSimilarQuestionModels extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalShow: false,
      similar: [],
      loading: false,
      error: null,
      currentIndex: 0,
      showExplanation: false,
    };
  }

  // ✅ cache key by question_id
  getCacheKey = () => {
    const questionId = this.props.question_id || "no_id";
    return `ai_similar_questions_${questionId}`;
  };

  open = () => this.setState({ modalShow: true });
  close = () =>
    this.setState({
      modalShow: false,
      currentIndex: 0,
      showExplanation: false,
    });

  loadFromCache = () => {
    try {
      const cached = localStorage.getItem(this.getCacheKey());
      if (!cached) return null;
      return JSON.parse(cached);
    } catch (e) {
      return null;
    }
  };

  saveToCache = (data) => {
    try {
      localStorage.setItem(this.getCacheKey(), JSON.stringify(data));
    } catch (e) {
      // ignore
    }
  };

  // ✅ ONLY TEXT should be sent to API
  cleanQuestionForApi = (input) => {
    if (!input) return "";

    let text = String(input);

    // ✅ 1) Remove HTML tags completely
    text = text.replace(/<\/?[^>]+(>|$)/g, " ");

    // ✅ 2) Convert common HTML entities
    text = text
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // ✅ 3) Remove MathML tags if present (send only text)
    text = text.replace(/<math[\s\S]*?<\/math>/g, " ");

    // ✅ 4) Normalize extra spaces
    text = text.replace(/\s+/g, " ").trim();

    return text;
  };

  fetchSimilarFromApi = async () => {
    const rawQuery = this.props.question || this.props.triggerQueryText || "sample question";

    console.log("rawQuery",rawQuery);
    

    // ✅ Clean question (ONLY TEXT) for API
    const query = this.cleanQuestionForApi(rawQuery);

    const studentClass = this.props.className || "11";
    const subject = this.props.subject || "maths";

    this.setState({
      loading: true,
      error: null,
      similar: [],
      currentIndex: 0,
      showExplanation: false,
    });

    this.open();

    try {
      const payload = {
        question: query, // ✅ clean text only
        student_class: studentClass,
        subject,
        limit: 3,
        difficulty: ["Difficult"],
      };

      const res = await fetch("https://ai.rizee.in/generate-mcqs-with-explanations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API error ${res.status}: ${text}`);
      }

      const data = await res.json();
      const questions = Array.isArray(data?.questions) ? data.questions : [];

      // ✅ save cache
      if (this.props.question_id) {
        this.saveToCache({
          storedAt: Date.now(),
          question_id: this.props.question_id,
          questions,
        });
      }

      this.setState({
        similar: questions,
        loading: false,
      });
    } catch (err) {
      this.setState({
        error: err.message || String(err),
        loading: false,
      });
    }
  };

  openSimilarQuestions = () => {
    if (!this.props.question_id) {
      this.fetchSimilarFromApi();
      return;
    }

    const cached = this.loadFromCache();

    if (cached?.questions && Array.isArray(cached.questions) && cached.questions.length > 0) {
      this.setState({
        modalShow: true,
        similar: cached.questions,
        loading: false,
        error: null,
        currentIndex: 0,
        showExplanation: false,
      });
    } else {
      this.fetchSimilarFromApi();
    }
  };

  componentDidUpdate(prevProps) {
    if (this.props.triggerQuery !== prevProps.triggerQuery) {
      this.openSimilarQuestions();
    }
  }

  // ✅ UI display using MathJax (render question/explanation nicely)
  renderMathContent = (text) => {
    const html = (marked.parse(text || "") || "").replace(/\n/g, "");
    return (
      <MathJax dynamic>
        <div
          style={styles.answerContent}
          dangerouslySetInnerHTML={{
            __html: html || "<p style='color:#777'>No content available.</p>",
          }}
        />
      </MathJax>
    );
  };

  nextQuestion = () => {
    this.setState((prev) => ({
      currentIndex: Math.min(prev.currentIndex + 1, prev.similar.length - 1),
      showExplanation: false,
    }));
  };

  prevQuestion = () => {
    this.setState((prev) => ({
      currentIndex: Math.max(prev.currentIndex - 1, 0),
      showExplanation: false,
    }));
  };

  render() {
    console.log("AiSimilarQuestionModels props",this.props);
    const { modalShow, similar, loading, error, currentIndex, showExplanation } = this.state;
    const current = similar[currentIndex] || null;

    const config = {
      loader: { load: ["[tex]/html"] },
      tex: {
        inlineMath: [["$", "$"], ["\\(", "\\)"]],
        displayMath: [["$$", "$$"], ["\\[", "\\]"]],
      },
      svg: { fontCache: "global" },
    };

    return (
      <MathJaxContext config={config}>
        <style>
          {`
            @media (max-width: 576px) {
              .ai-options-grid {
                grid-template-columns: 1fr !important;
              }
            }

            /* ✅ Compact header */
            .ai-modal-header-compact {
              padding: 10px 14px !important;
              min-height: 44px !important;
            }

            .ai-modal-title-compact {
              font-size: 15px !important;
              font-weight: 900 !important;
              margin: 0 !important;
              line-height: 1.2 !important;
            }
          `}
        </style>

        <Modal show={modalShow} onHide={this.close} size="lg" centered>
          <Modal.Header closeButton className="ai-modal-header-compact">
            <Modal.Title className="ai-modal-title-compact">
              📝 Similar Questions (MCQs):
            </Modal.Title>
          </Modal.Header>

          <Modal.Body style={styles.modalBody}>
            {loading && (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <div className="mt-2">Loading similar questions...</div>
              </div>
            )}

            {!loading && error && <Alert variant="danger">{error}</Alert>}

            {!loading && !error && similar.length === 0 && (
              <Alert variant="warning">No similar questions found.</Alert>
            )}

            {!loading && !error && current && (
              <>
                {/* ✅ Top Question (MathJax display) */}
                <div style={styles.topBar}>
                  <div style={styles.counterChip}>
                    {currentIndex + 1} / {similar.length}
                  </div>

                  <div style={styles.topQuestionBox}>
                    {/* <div style={{ fontWeight: 900, marginBottom: "6px" }}>Q:</div> */}
                    <div style={{ fontWeight: 600 }}>{this.renderMathContent(current.question)}</div>
                  </div>
                </div>

                <div style={styles.outputCard}>
                  {/* ✅ Options */}
                  <div style={styles.answerBox}>
                    <div style={styles.label}>✅ Choose the correct option</div>

                    <div className="ai-options-grid" style={styles.optionsGrid}>
                      {Object.entries(current.options || {}).map(([key, value]) => {
                        const isCorrect = key === current.correct_answer;

                        return (
                          <div
                            key={key}
                            style={{
                              ...styles.optionCard,
                              ...(isCorrect ? styles.correctOptionCard : {}),
                            }}
                          >
                            <div style={styles.optionInner}>
                              <b style={styles.optionKey}>{key}.</b>
                              <span style={styles.optionValue}>{value}</span>
                            </div>
                            {isCorrect && <span style={styles.tick}>✔</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ✅ Explanation Toggle */}
                  <div style={styles.actionsRow}>
                    <button
                      style={showExplanation ? styles.dangerButton : styles.smallButton}
                      onClick={() => this.setState((p) => ({ showExplanation: !p.showExplanation }))}
                    >
                      {showExplanation ? "Hide Explanation" : "Explain"}
                    </button>
                  </div>

                  {/* ✅ Explanation */}
                  {showExplanation && (
                    <div style={styles.explanationBox}>
                      <div style={styles.explanationHeading}>📌 Explanation</div>
                      <div style={styles.explanationText}>
                        {this.renderMathContent(current.explanation)}
                      </div>
                    </div>
                  )}

                  {/* ✅ Navigation */}
                  <div style={styles.navRow}>
                    <button
                      style={currentIndex === 0 ? styles.disabledNavBtn : styles.navBtn}
                      onClick={this.prevQuestion}
                      disabled={currentIndex === 0}
                    >
                      ⬅ Prev
                    </button>

                    <button
                      style={currentIndex === similar.length - 1 ? styles.disabledNavBtn : styles.navBtn}
                      onClick={this.nextQuestion}
                      disabled={currentIndex === similar.length - 1}
                    >
                      Next ➡
                    </button>
                  </div>
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
      </MathJaxContext>
    );
  }
}

/* ✅ FINAL UI STYLES */
const styles = {
  modalBody: {
    background: "linear-gradient(180deg,#f8fafc,#eef2ff)",
    padding: "18px",
  },

  topBar: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "12px",
  },

  counterChip: {
    alignSelf: "flex-start",
    background: "rgba(79,70,229,0.14)",
    border: "1px solid rgba(79,70,229,0.25)",
    color: "#312e81",
    padding: "6px 12px",
    borderRadius: "999px",
    fontWeight: 900,
    fontSize: "12px",
  },

  topQuestionBox: {
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(99,102,241,0.18)",
    padding: "10px 12px",
    borderRadius: "12px",
    fontWeight: 800,
    color: "#0f172a",
    fontSize: "14px",
    lineHeight: "1.4rem",
  },

  outputCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
    padding: "18px",
    border: "1px solid rgba(99,102,241,0.18)",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },

  answerBox: {
    background: "#fff",
    padding: "12px",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
  },

  label: {
    fontWeight: 900,
    fontSize: "13px",
    marginBottom: "8px",
    color: "#475569",
  },

  optionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "8px",
  },

  optionCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    padding: "8px 10px",
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    fontSize: "14px",
    lineHeight: "1.2rem",
  },

  correctOptionCard: {
    background: "rgba(16,185,129,0.12)",
    border: "1px solid rgba(16,185,129,0.40)",
    color: "#065f46",
    fontWeight: 800,
  },

  optionInner: {
    display: "flex",
    gap: "8px",
    flex: 1,
  },

  optionKey: {
    minWidth: "22px",
    fontWeight: 900,
  },

  optionValue: {
    flex: 1,
    fontWeight: 600,
  },

  tick: {
    fontWeight: 900,
  },

  actionsRow: {
    display: "flex",
    justifyContent: "flex-end",
  },

  smallButton: {
    padding: "8px 14px",
    borderRadius: "999px",
    border: "1px solid #6366f1",
    background: "#6366f1",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },

  dangerButton: {
    padding: "8px 14px",
    borderRadius: "999px",
    border: "1px solid #ef4444",
    background: "#ef4444",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },

  explanationBox: {
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    padding: "14px",
    borderRadius: "14px",
  },

  explanationHeading: {
    fontWeight: 900,
    fontSize: "13px",
    marginBottom: "8px",
    color: "#92400e",
  },

  explanationText: {
    color: "#92400e",
  },

  navRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "6px",
    gap: "10px",
  },

  navBtn: {
    padding: "10px 14px",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    minWidth: "120px",
  },

  disabledNavBtn: {
    padding: "10px 14px",
    borderRadius: "12px",
    background: "#e5e7eb",
    fontWeight: 900,
    opacity: 0.6,
    cursor: "not-allowed",
    minWidth: "120px",
  },

  answerContent: {
    lineHeight: "1.7rem",
    fontSize: "14px",
  },
};

export default AiSimilarQuestionModels;
